import { FC } from 'react';

import {
  ScheduledBankTransferEndDateDeterminedBy,
  ScheduledBankTransferFrequency,
} from '__generated__/GQL';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  BankTransferInputSchedule,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import { formatDate } from 'helpers/date';
import ErrorLayout from 'layouts/ErrorLayout';

import Success from '../../components/Success';
import useBankTransferData from '../../hooks/useBankTransferData';
import { computeLastDueDate } from '../../utils';
import locales from './locales';

const getScheduledTransferSchedulingDetails = (
  schedule: BankTransferInputSchedule,
) => {
  const {
    endDateDeterminedBy,
    frequency,
    lastDueDate,
    nextDueDate,
    remainingNumberOfOccurrences,
  } = schedule;

  if (frequency === ScheduledBankTransferFrequency.Once) {
    return locales.formatString(
      locales.scheduledTransferDetails.recurrentWithoutEndDate[frequency],
      {
        date: formatDate(new Date(nextDueDate), 'P'),
      },
    );
  }

  if (!endDateDeterminedBy) {
    return locales.formatString(
      locales.scheduledTransferDetails.recurrentWithoutEndDate[frequency],
      {
        startDate: formatDate(new Date(nextDueDate), 'P'),
      },
    );
  }

  if (
    endDateDeterminedBy ===
      ScheduledBankTransferEndDateDeterminedBy.NumberOfOccurrences &&
    !!remainingNumberOfOccurrences
  ) {
    return locales.formatString(
      locales.scheduledTransferDetails.recurrentWithEndDate[frequency],
      {
        endDate: formatDate(
          computeLastDueDate({
            frequency,
            nextDueDate,
            numberOfOccurrences: remainingNumberOfOccurrences,
          }),
          'P',
        ),
        startDate: formatDate(new Date(nextDueDate), 'P'),
      },
    );
  }

  if (
    endDateDeterminedBy ===
      ScheduledBankTransferEndDateDeterminedBy.ExplicitDate &&
    !!lastDueDate
  ) {
    return locales.formatString(
      locales.scheduledTransferDetails.recurrentWithEndDate[frequency],
      {
        endDate: formatDate(new Date(lastDueDate), 'P'),
        startDate: formatDate(new Date(nextDueDate), 'P'),
      },
    );
  }

  return locales.formatString(
    locales.scheduledTransferDetails.recurrentWithoutEndDate[frequency],
    {
      date: formatDate(new Date(nextDueDate), 'P'),
    },
  );
};

const getTransferLocales = ({
  isInstant,
  schedule,
}: {
  isInstant: boolean;
  schedule?: BankTransferInputSchedule;
}) => {
  if (schedule) {
    return {
      description: locales.external.scheduled.description,
      schedulingDetails: getScheduledTransferSchedulingDetails(schedule),
      title: locales.external.scheduled.title,
    };
  }

  if (isInstant) {
    return {
      description: locales.external.instant.description,
      details: locales.external.instant.details,
      title: locales.external.instant.title,
    };
  }

  return {
    description: locales.external.standard.description,
    details: locales.external.standard.details,
    title: locales.external.standard.title,
  };
};

const ExternalTransferSuccess: FC<StepProps<BankTransferCreationStep>> = () => {
  const { bankTransfer } = useBankTransferContext('externalTransferSuccess');

  const {
    amount,
    bankTransferRecipientId,
    fromBankAccountId,
    isInstant,
    schedule,
  } = bankTransfer;

  const { data, error, refetch } = useBankTransferData({
    bankTransferRecipientId,
    fromBankAccountId,
  });

  if (error) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetch} />
      </ErrorLayout>
    );
  }

  if (!data) {
    return <Loader />;
  }

  const {
    fromBankAccount,
    viewer: {
      company: { bankTransferRecipient },
    },
  } = data;

  const transferLocales = getTransferLocales({ isInstant, schedule });
  const transferDetails =
    transferLocales.details || transferLocales.schedulingDetails;

  return (
    <>
      <TrackView pageName="NewExternalTransferSuccess" />
      <Success
        amount={amount}
        fromBankAccount={fromBankAccount}
        recipient={bankTransferRecipient.name}
        transferLocales={{ ...transferLocales, transferDetails }}
      />
    </>
  );
};

export default ExternalTransferSuccess;
